// src/services/aiService.js
// Serviço de IA usando OpenAI API com Sistema Multiagente

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

const makeRequest = async (messages, maxTokens = 500, temperature = 0.7) => {
  const response = await fetch(OPENAI_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${OPENAI_API_KEY}`
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      messages,
      max_tokens: maxTokens,
      temperature
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Erro na API OpenAI');
  }

  const data = await response.json();
  return data.choices[0]?.message?.content || '';
};

// ==========================================
// FUNÇÃO DE LEITURA DE URLs E PDFs
// ==========================================

// Fetch e extração de conteúdo de PDFs via URL usando Jina Reader API
export const fetchPdfContent = async (pdfUrl) => {
  if (!pdfUrl || !pdfUrl.trim()) {
    return null;
  }

  try {
    // Jina Reader API suporta PDFs diretamente
    const jinaUrl = `https://r.jina.ai/${pdfUrl}`;

    const response = await fetch(jinaUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error(`Erro ao buscar PDF: ${response.status}`);
    }

    const content = await response.text();

    // Limitar tamanho do conteúdo para não exceder tokens
    const maxChars = 15000;
    if (content.length > maxChars) {
      return {
        content: content.substring(0, maxChars) + '\n\n[... conteúdo truncado ...]',
        truncated: true,
        totalChars: content.length
      };
    }

    return {
      content,
      truncated: false,
      totalChars: content.length
    };
  } catch (error) {
    console.error('Erro ao buscar conteúdo do PDF:', error);
    throw new Error(`Não foi possível ler o PDF: ${error.message}`);
  }
};

// Extração de texto de PDF local usando pdf.js via CDN
export const extractPdfText = async (file) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Carregar pdf.js do CDN se não estiver carregado
      if (!window.pdfjsLib) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          processFile();
        };
        script.onerror = () => reject(new Error('Falha ao carregar biblioteca PDF'));
        document.head.appendChild(script);
      } else {
        processFile();
      }

      async function processFile() {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

          let fullText = '';
          const totalPages = pdf.numPages;

          for (let i = 1; i <= totalPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\n\n';
          }

          // Limitar tamanho
          const maxChars = 15000;
          const truncated = fullText.length > maxChars;

          resolve({
            content: truncated ? fullText.substring(0, maxChars) + '\n\n[... conteúdo truncado ...]' : fullText,
            truncated,
            totalChars: fullText.length,
            totalPages
          });
        } catch (error) {
          reject(new Error(`Erro ao processar PDF: ${error.message}`));
        }
      }
    } catch (error) {
      reject(error);
    }
  });
};

// Gerar artigo a partir de conteúdo de PDF
export const generatePostFromPdf = async (pdfContent, topic = '', team = 'educativa', onProgress = null) => {
  try {
    // FASE 1: Analisar e estruturar o conteúdo do PDF
    if (onProgress) onProgress('analyzing', 'Analisando conteúdo do PDF...');

    const analyzerMessages = [
      {
        role: 'system',
        content: `Você é um analista de conteúdo especializado em documentos técnicos e acadêmicos.

SUA TAREFA: Analisar o conteúdo extraído de um PDF e criar um resumo estruturado.

EXTRAIA:
1. TEMA PRINCIPAL do documento (será usado como título)
2. PONTOS-CHAVE (5-8 pontos mais importantes)
3. DADOS E ESTATÍSTICAS relevantes (com contexto)
4. CITAÇÕES importantes (se houver)
5. CONCLUSÕES ou recomendações

FORMATO DE SAÍDA (JSON):
{
  "tema": "Tema principal do documento",
  "titulo_sugerido": "Título atraente para blog",
  "pontos_chave": ["ponto 1", "ponto 2", ...],
  "dados_relevantes": ["dado 1", "dado 2", ...],
  "citacoes": ["citação 1", "citação 2", ...],
  "conclusoes": ["conclusão 1", "conclusão 2", ...],
  "palavras_chave": ["palavra1", "palavra2", ...]
}

Retorne APENAS o JSON, sem explicações.`
      },
      {
        role: 'user',
        content: `Analise este conteúdo extraído de um PDF:\n\n${pdfContent.substring(0, 15000)}`
      }
    ];

    const analysisRaw = await makeRequest(analyzerMessages, 2500, 0.3);

    // Parsear análise
    let analysis;
    try {
      analysis = JSON.parse(analysisRaw.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      analysis = { tema: topic || 'Documento analisado', raw: analysisRaw };
    }

    // FASE 2: Gerar artigo usando a equipe selecionada
    if (onProgress) onProgress('generating', 'Gerando artigo com a equipe de redação...');

    // Usar o tema extraído ou o fornecido pelo usuário
    const finalTopic = topic || analysis.titulo_sugerido || analysis.tema || 'Insights do documento analisado';

    // Criar contexto de referência com a análise do PDF
    const referenceContext = `
=== ANÁLISE DO DOCUMENTO PDF ===
Use as informações abaixo como BASE FACTUAL para o artigo.
Mantenha os dados e estatísticas fiéis ao documento original.
O artigo deve ter NO MÍNIMO 1500 palavras, explorando todos os pontos-chave.

TEMA: ${analysis.tema || finalTopic}

PONTOS-CHAVE A ABORDAR:
${(analysis.pontos_chave || []).map((p, i) => `${i+1}. ${p}`).join('\n')}

DADOS E ESTATÍSTICAS PARA USAR:
${(analysis.dados_relevantes || []).map(d => `- ${d}`).join('\n')}

CONCLUSÕES DO DOCUMENTO:
${(analysis.conclusoes || []).map(c => `- ${c}`).join('\n')}

=== FIM DA ANÁLISE ===

IMPORTANTE: Desenvolva CADA ponto-chave em pelo menos 2 parágrafos. O artigo deve ser completo e aprofundado.
`;

    // Gerar artigo com a equipe apropriada
    const article = await generatePostWithTeam(
      finalTopic,
      (analysis.palavras_chave || []).join(', '),
      team,
      (stage, message) => {
        if (onProgress) onProgress(stage, message);
      },
      referenceContext
    );

    // FASE 3: Gerar metadados do post
    if (onProgress) onProgress('metadata', 'Gerando título, excerpt e tags...');

    const metadataMessages = [
      {
        role: 'system',
        content: `Você é um especialista em SEO e marketing de conteúdo B2B.

Gere metadados otimizados para um post de blog sobre tecnologia industrial.

FORMATO DE SAÍDA (JSON):
{
  "titulo": "Título atraente com 50-70 caracteres, inclua números se possível",
  "excerpt": "Resumo de 150-200 caracteres que desperte curiosidade",
  "meta_description": "Descrição SEO de 150-160 caracteres com call-to-action",
  "tags": ["tag1", "tag2", "tag3", "tag4", "tag5"],
  "categoria": "tecnologia|automacao|industria-4-0|cases|tutoriais"
}

Retorne APENAS o JSON.`
      },
      {
        role: 'user',
        content: `Gere metadados para este artigo sobre "${finalTopic}":\n\n${article.substring(0, 2000)}`
      }
    ];

    const metadataRaw = await makeRequest(metadataMessages, 800, 0.5);

    let metadata;
    try {
      metadata = JSON.parse(metadataRaw.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      metadata = {
        titulo: finalTopic,
        excerpt: article.substring(0, 200).replace(/[#*]/g, '').trim() + '...',
        meta_description: `Saiba mais sobre ${finalTopic}. Conteúdo completo e atualizado.`,
        tags: analysis.palavras_chave || ['tecnologia', 'indústria', 'inovação'],
        categoria: 'tecnologia'
      };
    }

    if (onProgress) onProgress('completed', 'Artigo gerado com sucesso!');

    return {
      article,
      title: metadata.titulo,
      excerpt: metadata.excerpt,
      meta_description: metadata.meta_description,
      tags: metadata.tags,
      category: metadata.categoria,
      analysis,
      sourcePdf: true
    };

  } catch (error) {
    console.error('Erro ao gerar artigo do PDF:', error);
    throw error;
  }
};

// Fetch e extração de conteúdo de URLs usando Jina Reader API (gratuito)
export const fetchUrlContent = async (url) => {
  if (!url || !url.trim()) {
    return null;
  }

  try {
    // Jina Reader API - converte páginas web em texto limpo
    const jinaUrl = `https://r.jina.ai/${url}`;

    const response = await fetch(jinaUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error(`Erro ao buscar URL: ${response.status}`);
    }

    const content = await response.text();

    // Limitar tamanho do conteúdo para não exceder tokens
    const maxChars = 8000;
    if (content.length > maxChars) {
      return content.substring(0, maxChars) + '\n\n[... conteúdo truncado ...]';
    }

    return content;
  } catch (error) {
    console.error('Erro ao buscar conteúdo da URL:', error);
    throw new Error(`Não foi possível ler a URL: ${error.message}`);
  }
};

// Função auxiliar para criar contexto de referência
const buildReferenceContext = (referenceContent) => {
  if (!referenceContent) return '';

  return `
=== ARTIGO DE REFERÊNCIA ===
Use o artigo abaixo como REFERÊNCIA de estilo, dados e estrutura.
NÃO copie o texto - use como inspiração para criar conteúdo ORIGINAL.
Extraia: tom, estrutura, tipos de dados citados, formato de citações.

${referenceContent}

=== FIM DA REFERÊNCIA ===

`;
};

// ==========================================
// CONTEXTO COMPLETO DA VEGA ROBOTICS E DIVISÕES
// ==========================================

// Informações da empresa e divisões (para referência interna nos prompts)
export const VEGA_CONTEXT = {
  empresa: {
    nome: 'Vega Robotics',
    slogan: 'Tecnologia para Indústria Inteligente',
    fundacao: 2019,
    sede: 'Campinas, SP - Brasil',
    reconhecimentos: ['NVIDIA Inception Program'],
    site: 'https://www.vegarobotics.com.br',
    linkedin: 'https://www.linkedin.com/company/vegarobotics'
  },

  divisoes: {
    vegaCore: {
      nome: 'Vega Robotics (Core)',
      foco: 'Soluções de IA e automação industrial',
      servicos: [
        'Plataformas de IA Industrial (análise preditiva, otimização de processos)',
        'Visão Computacional (inspeção de qualidade, detecção de defeitos)',
        'Edge Computing (processamento local, dispositivos inteligentes)',
        'Consultoria em Indústria 4.0 (transformação digital)'
      ],
      diferenciais: [
        'Soluções integradas e customizáveis',
        'Tecnologia desenvolvida no Brasil para realidade brasileira',
        'Time com experiência em grandes indústrias',
        'Membro do NVIDIA Inception Program'
      ]
    },

    cadpro: {
      nome: 'CADPro - Engenharia Aplicada',
      foco: 'Terceirização de engenharia e projetos sob demanda',
      site: 'https://cadpro.vegarobotics.com.br',
      servicos: [
        'Projeto de ferramentas de estampagem',
        'Dispositivos de controle e fixação',
        'Detalhamento técnico completo',
        'Projetos mecânicos especiais',
        'Análise e planejamento de projetos'
      ],
      diferenciais: [
        'Mais de 15 anos de experiência em projetos industriais',
        'Modelo flexível: contrate capacidade, não equipe fixa',
        'Entrega ágil e comunicação direta',
        'Especialização em setor automotivo e metalúrgico'
      ],
      setoresAtendidos: [
        'Automotivo (montadoras e Tier 1)',
        'Metalurgia e estampagem',
        'Máquinas e equipamentos',
        'Manufatura em geral'
      ]
    },

    proagentes: {
      nome: 'PROAgentes.ai',
      foco: 'Agentes de IA para automação de processos',
      site: 'https://proagentes.ai',
      servicos: [
        'Agentes de IA para atendimento ao cliente',
        'Automação de processos administrativos',
        'Integração com sistemas existentes (ERP, CRM)',
        'Chatbots inteligentes para B2B'
      ],
      diferenciais: [
        'IA generativa aplicada ao contexto industrial',
        'Integração com dados da empresa',
        'Disponível 24/7'
      ]
    }
  },

  setoresFoco: [
    'Indústria automotiva (montadoras, Tier 1, fornecedores)',
    'Metalurgia e siderurgia',
    'Manufatura discreta',
    'Logística e centros de distribuição',
    'Máquinas e equipamentos industriais'
  ],

  proposta: 'Ajudar indústrias brasileiras a implementar tecnologia de forma prática, acessível e com resultados mensuráveis.'
};

// Contexto dos SERVIÇOS e CONCEITOS (foco em educar, não em vender marca)
const SERVICOS_CONTEXT = `
TIPOS DE SERVIÇOS E SOLUÇÕES QUE EXISTEM NO MERCADO:

1. **Plataformas de IA Industrial** - Sistemas que analisam dados de produção em tempo real, preveem falhas em equipamentos, otimizam processos e geram recomendações inteligentes para gestores. Conceitos: manutenção preditiva, análise de dados industriais, dashboards em tempo real, machine learning aplicado à produção.

2. **Visão Computacional e Edge Computing** - Dispositivos inteligentes que "enxergam" a linha de produção: fazem inspeção automática de qualidade, contagem de peças, detecção de defeitos, monitoramento de segurança. Conceitos: processamento na borda, câmeras inteligentes, deep learning para visão, controle de qualidade automatizado.

3. **Engenharia sob Demanda / Terceirização de Engenharia** - Modelo de contratação flexível onde empresas acessam capacidade de engenharia sem contratar equipe fixa. Inclui: projeto de ferramentas de estampagem, dispositivos de controle, detalhamento técnico, projetos mecânicos. Conceitos: engenharia escalável, outsourcing técnico, projetos sob demanda.

4. **Consultoria em Indústria 4.0** - Diagnóstico e implementação de transformação digital em fábricas. Inclui: mapeamento de processos, identificação de gargalos, roadmap de automação, integração de sistemas. Conceitos: digitalização industrial, IoT industrial, automação inteligente, lean manufacturing digital.

5. **Agentes de IA / Automação Inteligente** - Sistemas baseados em IA generativa que automatizam tarefas repetitivas, respondem perguntas complexas, integram-se a ERPs e CRMs, e funcionam como assistentes virtuais especializados. Conceitos: IA generativa, chatbots B2B, automação de processos, assistentes virtuais.

SETORES QUE MAIS BUSCAM ESSAS SOLUÇÕES:
- Indústria automotiva (montadoras e fornecedores Tier 1)
- Metalurgia e siderurgia
- Manufatura discreta e de processo
- Logística e centros de distribuição
- Máquinas e equipamentos industriais

DIVISÕES DA VEGA ROBOTICS (usar quando relevante, de forma natural):

**Vega Robotics (Core)** - Foco em IA industrial, visão computacional e consultoria em Indústria 4.0. Membro do NVIDIA Inception Program.

**CADPro - Engenharia Aplicada** - Terceirização de engenharia mecânica, projetos de ferramentas de estampagem, dispositivos e detalhamento técnico. Mais de 15 anos de experiência. Site: cadpro.vegarobotics.com.br

**PROAgentes.ai** - Agentes de IA para automação de processos empresariais, atendimento ao cliente e integração com sistemas. Site: proagentes.ai

SOBRE A VEGA ROBOTICS (usar apenas no final, de forma sutil):
A Vega Robotics é uma empresa brasileira, membro do NVIDIA Inception Program, que oferece soluções integradas de tecnologia industrial - desde IA e visão computacional até engenharia sob demanda - ajudando indústrias a implementar inovação de forma prática e acessível.
`;

// ==========================================
// SISTEMA MULTIAGENTE PARA GERAÇÃO DE CONTEÚDO
// ==========================================

// AGENTE 1: REDATOR TÉCNICO INDUSTRIAL
const REDATOR_SYSTEM = `Você é um REDATOR TÉCNICO especializado em conteúdo educativo para a indústria brasileira.

PERFIL:
- 15 anos de experiência escrevendo para setores automotivo, metalúrgico e manufatura
- Conhecimento profundo de processos industriais, lean manufacturing, Indústria 4.0
- Estilo: informativo, técnico mas acessível, baseado em dados e exemplos práticos
- Você educa o leitor sobre CONCEITOS e TIPOS DE SOLUÇÃO, não sobre marcas

${SERVICOS_CONTEXT}

DIRETRIZES:
- Escreva em português brasileiro fluido e profissional
- Use linguagem técnica quando apropriado, mas sempre explicando termos complexos
- Inclua dados, estatísticas e exemplos reais do cenário industrial brasileiro
- Organize com subtítulos (##) claros e parágrafos curtos (3-4 linhas)
- O texto deve ter entre 800-1000 palavras
- Foque nos PROBLEMAS REAIS que a indústria enfrenta
- Explique os CONCEITOS e TIPOS DE SOLUÇÃO que existem (IA industrial, visão computacional, engenharia sob demanda, etc.)
- NÃO mencione nenhuma marca ou empresa - apenas eduque sobre as soluções disponíveis no mercado
- O leitor deve terminar entendendo O QUE são essas tecnologias e COMO elas resolvem problemas`;

// AGENTE 2: ESPECIALISTA EM MARKETING B2B
const MARKETEIRO_SYSTEM = `Você é um ESPECIALISTA EM MARKETING DE CONTEÚDO B2B para o setor industrial.

PERFIL:
- 12 anos de experiência em marketing de conteúdo para tecnologia industrial
- Especialista em inbound marketing e geração de leads qualificados
- Abordagem: educativa primeiro, comercial só no final e de forma muito sutil

${SERVICOS_CONTEXT}

SUA TAREFA:
Você receberá um artigo técnico educativo. Sua missão é:

1. MANTER o foco educativo em CONCEITOS e TIPOS DE SERVIÇO (não em produtos específicos)
2. REFORÇAR os benefícios práticos das soluções mencionadas (IA industrial, visão computacional, engenharia sob demanda)
3. ADICIONAR apenas NO FINAL uma seção curta "## Próximos Passos" ou "## Como Começar"
4. Nessa seção final, mencionar de forma BREVE e NATURAL que a Vega Robotics pode ajudar

DIRETRIZES IMPORTANTES:
- O artigo deve ser 95% educativo e apenas 5% comercial
- NÃO mencione "Vega Robotics" no corpo do texto - apenas na seção final
- NÃO mencione nomes de produtos (Vega Copilot, Device Vega, CADPro) - fale dos CONCEITOS
- Use termos genéricos: "plataformas de IA industrial", "soluções de visão computacional", "parceiros de engenharia"
- A seção final deve ser curta (2-3 parágrafos) e convidar para uma conversa exploratória
- Tom: "Se você quer explorar como essas soluções se aplicam à sua realidade, a Vega Robotics pode ajudar..."
- CTA suave: convidar para conversa, não para compra`;

// AGENTE 3: REVISOR EDITORIAL SÊNIOR
const REVISOR_SYSTEM = `Você é um REVISOR EDITORIAL SÊNIOR especializado em conteúdo educativo B2B.

PERFIL:
- 20 anos de experiência em revisão editorial para publicações de negócios e tecnologia
- Especialista em garantir que conteúdo seja genuinamente educativo, não promocional
- Exigente com clareza, fluidez e valor real para o leitor

${SERVICOS_CONTEXT}

SUA TAREFA:
Revisar o artigo final garantindo:

1. FOCO EDUCATIVO: O texto ensina conceitos? O leitor aprende algo de valor?
2. FLUIDEZ: O texto flui naturalmente? As transições são suaves?
3. EQUILÍBRIO: O artigo é 95% educação e apenas 5% menção à empresa?
4. NATURALIDADE: A menção à Vega Robotics no final parece natural e não forçada?
5. CREDIBILIDADE: O texto passa confiança? Parece escrito por especialista?
6. SEM PRODUTOS: Não há menção a nomes de produtos específicos? (só conceitos)

CHECKLIST DE REVISÃO:
- [ ] "Vega Robotics" aparece APENAS na seção final
- [ ] Nenhum nome de produto (Vega Copilot, Device Vega, CADPro) aparece
- [ ] O texto fala de "IA industrial", "visão computacional", "engenharia sob demanda" (conceitos)
- [ ] O CTA é um convite para conversa, não uma venda
- [ ] O leitor termina mais informado sobre o tema

DIRETRIZES:
- Corrija erros gramaticais e de concordância
- Se encontrar menções promocionais no corpo do texto, remova ou torne educativas
- Se a menção à Vega parecer forçada, suavize ainda mais
- O texto final deve parecer artigo de revista especializada, não propaganda
- Retorne APENAS o texto revisado, sem comentários`;

// ==========================================
// EQUIPE 2: ESTILO JORNALÍSTICO (CNI)
// ==========================================

// AGENTE 2.1: PESQUISADOR DE DADOS
const PESQUISADOR_DADOS_SYSTEM = `Você é um JORNALISTA DE DADOS especializado em economia e indústria brasileira.

PERFIL:
- 10 anos de experiência em jornalismo de dados para veículos como Valor Econômico, Exame, InfoMoney
- Especialista em traduzir dados complexos em narrativas claras
- Fontes frequentes: IBGE, CNI, ABIMAQ, FIESP, Banco Mundial, OCDE, McKinsey, Deloitte

SUA TAREFA:
Receber um tema e criar um ESQUELETO FACTUAL do artigo com:

1. DADOS E RANKINGS (FONTES REAIS E VERIFICÁVEIS)
   - Use dados de fontes REAIS e conhecidas (IBGE, CNI, FIESP, consultorias, etc.)
   - SEMPRE cite a fonte junto com o dado: "Segundo a CNI, 72% das indústrias..."
   - Inclua o ano/período do dado quando possível
   - Se não tiver dado exato, use tendências conhecidas com ressalva: "Estudos indicam que..."
   - Evite inventar números específicos sem fonte - prefira tendências gerais

2. FONTES DE AUTORIDADE (REAIS OU VEROSSÍMEIS)
   - Prefira citar entidades reais (CNI, ABIMAQ, FIESP, SENAI, etc.)
   - Para especialistas, use cargos genéricos mas realistas
   - Ex: "Especialistas do setor apontam..." ou "Segundo executivos da indústria..."
   - Se criar nome fictício, deixe claro o cargo e contexto

3. ESTRUTURA DO ARTIGO
   - Título com dado impactante + fonte explícita
   - Lead com contexto metodológico (quem pesquisou, quando, como)
   - 4-5 seções temáticas com subtítulos descritivos
   - Pontos de inserção para citações com atribuição

FORMATO DE SAÍDA:
\`\`\`
TÍTULO: [título com dado + fonte]
LEAD: [contextualização com metodologia em 2-3 linhas]

DADOS PRINCIPAIS (com fonte):
- [dado 1] - Fonte: [nome da fonte, ano]
- [dado 2] - Fonte: [nome da fonte, ano]
- [dado 3] - Fonte: [nome da fonte, ano]

FONTES/ESPECIALISTAS:
- [Nome/Cargo, Instituição] - contexto da citação
- [Entidade] - tipo de dado/pesquisa disponível

ESTRUTURA:
1. [Subtítulo descritivo] - [pontos a cobrir + fontes a usar]
2. [Subtítulo descritivo] - [pontos a cobrir + fontes a usar]
3. [Subtítulo descritivo] - [pontos a cobrir + fontes a usar]
4. [Subtítulo descritivo] - [pontos a cobrir + fontes a usar]
\`\`\`
`;

// AGENTE 2.2: REPÓRTER SETORIAL
const REPORTER_SETORIAL_SYSTEM = `Você é um REPÓRTER SETORIAL especializado em indústria e economia brasileira.

PERFIL:
- 12 anos cobrindo setor industrial para grandes veículos
- Estilo: jornalístico, factual, pirâmide invertida
- Especialidade: transformar dados em narrativas com impacto humano

REFERÊNCIA DE ESTILO (artigo CNI):
- Título impactante com dado + fonte EXPLÍCITA
- Lead que expande o título com metodologia (quem pesquisou, quando, amostra)
- Corpo em pirâmide invertida (mais importante primeiro)
- Subtítulos descritivos que contam uma história
- Citações diretas com aspas, nome COMPLETO e cargo
- Tom neutro, informativo, sem opinião pessoal
- Comparações Brasil vs outros países
- Parágrafos curtos (3-4 linhas)

ESTRUTURA OBRIGATÓRIA:
1. Parágrafo de abertura com o fato principal + FONTE
2. Contextualização (quem fez a pesquisa, metodologia, período)
3. Detalhamento dos dados principais COM FONTES
4. Citação de autoridade #1 (com aspas e atribuição completa)
5. Aprofundamento setorial com dados adicionais
6. Citação de autoridade #2 (com aspas e atribuição completa)
7. Dados adicionais por setor/área
8. Perspectivas ou próximos passos

REGRAS DE CITAÇÃO E FONTES:
- SEMPRE cite a fonte dos dados: "Segundo o IBGE...", "De acordo com a CNI...", "Pesquisa da FIESP aponta..."
- Citações de pessoas SEMPRE com nome, cargo e instituição entre aspas
- Dados numéricos OBRIGATORIAMENTE com fonte e período
- Se não tiver fonte específica, use: "Especialistas do setor indicam...", "Levantamentos recentes apontam..."
- NUNCA apresente um número sem indicar de onde veio

REGRAS GERAIS:
- Escreva entre 800-1200 palavras
- Use subtítulos a cada 3-4 parágrafos
- NÃO use primeira pessoa
- NÃO dê opinião - apenas relate fatos
- NÃO mencione a Vega Robotics - isso será adicionado depois
`;

// AGENTE 2.3: EDITOR-CHEFE
const EDITOR_CHEFE_SYSTEM = `Você é um EDITOR-CHEFE de uma publicação de economia e negócios.

PERFIL:
- 20 anos de experiência editorial em veículos como Valor, Exame, Época Negócios
- Rigoroso com qualidade jornalística e ética editorial
- Especialista em conteúdo que gera credibilidade e engajamento

SUA TAREFA:
Revisar o artigo jornalístico e:

1. QUALIDADE JORNALÍSTICA E CITAÇÕES
   - Verificar estrutura de pirâmide invertida
   - Garantir que subtítulos sejam descritivos e com gancho
   - Confirmar que citações têm aspas e atribuição COMPLETA (nome, cargo, instituição)
   - Checar fluidez e transições entre seções
   - IMPORTANTE: Todas as fontes de dados devem ser citadas de forma transparente
   - Usar formato: "Segundo [fonte], ..." ou "De acordo com [pesquisa/estudo], ..."
   - Dados numéricos SEMPRE com fonte explícita

2. ADICIONAR SEÇÃO DE CONCLUSÃO COM CTA B2B
   Após o conteúdo jornalístico, adicionar uma seção separada:

   ---

   ## Como a Vega Robotics pode ajudar

   [Parágrafo conectando o tema do artigo com as soluções da Vega Robotics. Mencionar 2-3 soluções relevantes dentre:]
   - Plataformas de IA industrial para análise de dados e manutenção preditiva
   - Visão computacional e edge computing para controle de qualidade
   - Engenharia sob demanda para projetos de ferramentaria e dispositivos
   - Consultoria em Indústria 4.0 e transformação digital

   [Parágrafo de CTA convidando para uma conversa exploratória]

   **Quer saber como implementar essas soluções na sua indústria?**
   [Fale com nossos especialistas](#fale-conosco)

3. CHECKLIST FINAL
   - [ ] Título tem dado + fonte?
   - [ ] Lead contextualiza a metodologia?
   - [ ] Há pelo menos 2 citações com aspas e atribuição completa?
   - [ ] Todas as fontes de dados estão citadas?
   - [ ] Subtítulos são descritivos?
   - [ ] Seção da Vega conecta bem com o tema?
   - [ ] CTA usa o link especial #fale-conosco?

IMPORTANTE:
- O artigo deve parecer de um veículo jornalístico sério
- A seção da Vega deve ser APÓS o conteúdo jornalístico (separada por ---)
- O CTA DEVE usar exatamente: [Fale com nossos especialistas](#fale-conosco)
- NÃO adicionar editorias ou tags no final - apenas o artigo e a seção da Vega
- Retorne APENAS o artigo finalizado com a seção da Vega
`;

// Função principal da Equipe 2: Geração Jornalística
export const generatePostJornalistico = async (topic, keywords = '', onProgress = null, referenceContent = null) => {
  try {
    // Construir contexto de referência se houver URL
    const referenceContext = buildReferenceContext(referenceContent);

    // FASE 1: Pesquisador estrutura os dados
    if (onProgress) onProgress('pesquisador', 'Pesquisador de dados estruturando informações...');

    const pesquisadorMessages = [
      { role: 'system', content: PESQUISADOR_DADOS_SYSTEM },
      {
        role: 'user',
        content: `${referenceContext}Crie o esqueleto factual para um artigo jornalístico sobre: ${topic}${keywords ? `\n\nPalavras-chave para considerar: ${keywords}` : ''}`
      }
    ];

    const esqueletoFactual = await makeRequest(pesquisadorMessages, 2500, 0.7);

    // FASE 2: Repórter escreve o artigo
    if (onProgress) onProgress('reporter', 'Repórter setorial escrevendo artigo...');

    const reporterMessages = [
      { role: 'system', content: REPORTER_SETORIAL_SYSTEM },
      {
        role: 'user',
        content: `Com base neste esqueleto factual, escreva um artigo jornalístico completo:\n\n${esqueletoFactual}`
      }
    ];

    const artigoBase = await makeRequest(reporterMessages, 4000, 0.6);

    // FASE 3: Editor-chefe finaliza
    if (onProgress) onProgress('editor', 'Editor-chefe revisando e finalizando...');

    const editorMessages = [
      { role: 'system', content: EDITOR_CHEFE_SYSTEM },
      {
        role: 'user',
        content: `Revise e finalize este artigo jornalístico, adicionando o box institucional e taxonomia:\n\n${artigoBase}`
      }
    ];

    const artigoFinal = await makeRequest(editorMessages, 4500, 0.4);

    if (onProgress) onProgress('concluido', 'Artigo jornalístico finalizado!');

    return artigoFinal;

  } catch (error) {
    console.error('Erro na equipe jornalística:', error);
    throw error;
  }
};

// Função unificada para escolher equipe
export const generatePostWithTeam = async (topic, keywords = '', team = 'educativa', onProgress = null, referenceContent = null) => {
  if (team === 'jornalistica') {
    return generatePostJornalistico(topic, keywords, onProgress, referenceContent);
  }
  return generatePostMultiAgent(topic, keywords, onProgress, referenceContent);
};

// Informações das equipes disponíveis
export const TEAMS_INFO = {
  educativa: {
    id: 'educativa',
    name: 'Equipe Educativa',
    description: 'Conteúdo técnico e tutorial focado em conceitos e soluções',
    agents: ['Redator Técnico', 'Especialista Marketing B2B', 'Revisor Editorial'],
    bestFor: 'Artigos "como fazer", explicações técnicas, guias práticos',
    tone: 'Educativo e consultivo'
  },
  jornalistica: {
    id: 'jornalistica',
    name: 'Equipe Jornalística',
    description: 'Estilo editorial com dados, rankings e citações de especialistas',
    agents: ['Pesquisador de Dados', 'Repórter Setorial', 'Editor-Chefe'],
    bestFor: 'Notícias, tendências, pesquisas, análises de mercado',
    tone: 'Jornalístico e factual'
  }
};

// ==========================================
// EQUIPE 1: ESTILO EDUCATIVO (ORIGINAL)
// ==========================================

// Função principal: Geração Multiagente
export const generatePostMultiAgent = async (topic, keywords = '', onProgress = null, referenceContent = null) => {
  try {
    // Construir contexto de referência se houver URL
    const referenceContext = buildReferenceContext(referenceContent);

    // FASE 1: Redator cria o conteúdo técnico base
    if (onProgress) onProgress('redator', 'Redator técnico criando conteúdo educativo...');

    const redatorMessages = [
      { role: 'system', content: REDATOR_SYSTEM },
      {
        role: 'user',
        content: `${referenceContext}Escreva um artigo técnico educativo sobre: ${topic}${keywords ? `\n\nPalavras-chave para incluir naturalmente: ${keywords}` : ''}`
      }
    ];

    const conteudoBase = await makeRequest(redatorMessages, 3500, 0.7);

    // FASE 2: Marketeiro integra as soluções de forma natural
    if (onProgress) onProgress('marketeiro', 'Especialista em marketing integrando soluções...');

    const marketeiroMessages = [
      { role: 'system', content: MARKETEIRO_SYSTEM },
      {
        role: 'user',
        content: `Aqui está o artigo técnico para você aprimorar com as soluções da Vega Robotics:\n\n${conteudoBase}`
      }
    ];

    const conteudoComMarketing = await makeRequest(marketeiroMessages, 4000, 0.6);

    // FASE 3: Revisor faz a revisão final
    if (onProgress) onProgress('revisor', 'Revisor sênior finalizando o artigo...');

    const revisorMessages = [
      { role: 'system', content: REVISOR_SYSTEM },
      {
        role: 'user',
        content: `Revise e aprimore este artigo final:\n\n${conteudoComMarketing}`
      }
    ];

    const conteudoFinal = await makeRequest(revisorMessages, 4500, 0.4);

    if (onProgress) onProgress('concluido', 'Artigo finalizado com sucesso!');

    return conteudoFinal;

  } catch (error) {
    console.error('Erro no sistema multiagente:', error);
    throw error;
  }
};

// Manter a função antiga para compatibilidade (agora usa multiagente)
export const generatePost = async (topic, keywords = '') => {
  return generatePostMultiAgent(topic, keywords);
};

// Gerar resumo inteligente do conteúdo
export const generateExcerpt = async (content) => {
  const messages = [
    {
      role: 'system',
      content: 'Você é um redator especializado em criar resumos concisos e atrativos para blogs de tecnologia e indústria. Responda apenas com o resumo, sem explicações adicionais.'
    },
    {
      role: 'user',
      content: `Crie um resumo atrativo de no máximo 160 caracteres para o seguinte conteúdo de blog:\n\n${content}`
    }
  ];

  return makeRequest(messages, 100);
};

// Gerar sugestões de títulos
export const generateTitles = async (content) => {
  const messages = [
    {
      role: 'system',
      content: 'Você é um redator especializado em criar títulos atrativos para blogs de tecnologia industrial. Responda apenas com os títulos, um por linha, numerados.'
    },
    {
      role: 'user',
      content: `Sugira 5 títulos criativos e otimizados para SEO para o seguinte conteúdo:\n\n${content}`
    }
  ];

  const response = await makeRequest(messages, 300);
  return response.split('\n').filter(line => line.trim()).map(line => line.replace(/^\d+\.\s*/, ''));
};

// Gerar meta description para SEO
export const generateMetaDescription = async (title, content) => {
  const messages = [
    {
      role: 'system',
      content: 'Você é um especialista em SEO. Crie meta descriptions otimizadas para buscadores. Responda apenas com a meta description, sem explicações.'
    },
    {
      role: 'user',
      content: `Crie uma meta description de no máximo 155 caracteres para:\n\nTítulo: ${title}\n\nConteúdo: ${content.substring(0, 500)}...`
    }
  ];

  return makeRequest(messages, 100);
};

// Sugerir tags baseadas no conteúdo
export const generateTags = async (content) => {
  const messages = [
    {
      role: 'system',
      content: 'Você é um especialista em categorização de conteúdo. Sugira tags relevantes para artigos de tecnologia e indústria. Responda apenas com as tags separadas por vírgula.'
    },
    {
      role: 'user',
      content: `Sugira 5-8 tags relevantes para o seguinte conteúdo:\n\n${content.substring(0, 1000)}`
    }
  ];

  return makeRequest(messages, 100);
};

// Melhorar/reescrever texto
export const improveText = async (text) => {
  const messages = [
    {
      role: 'system',
      content: 'Você é um editor profissional. Melhore o texto mantendo o significado original, tornando-o mais claro, fluido e profissional. Responda apenas com o texto melhorado.'
    },
    {
      role: 'user',
      content: `Melhore o seguinte texto:\n\n${text}`
    }
  ];

  return makeRequest(messages, Math.max(text.length * 2, 500));
};

// ==========================================
// LINKEDIN - GERAÇÃO DE CONTEÚDO OTIMIZADO
// ==========================================

// Gerar versão otimizada para LinkedIn
export const generateLinkedInPost = async (title, content, blogUrl = '') => {
  const messages = [
    {
      role: 'system',
      content: `Você é um especialista em marketing de conteúdo B2B no LinkedIn.

SUA TAREFA: Criar um post otimizado para LinkedIn a partir de um artigo de blog.

ESTRUTURA DO POST LINKEDIN (máximo 3000 caracteres):

1. GANCHO (primeira linha)
   - Pergunta provocativa OU dado impactante OU afirmação ousada
   - Deve fazer o leitor parar de rolar o feed
   - Ex: "72% das indústrias brasileiras ainda não usam IA. E você?"

2. CONTEXTO (2-3 parágrafos curtos)
   - Desenvolva o tema principal do artigo
   - Use parágrafos de 1-2 linhas (fácil leitura no mobile)
   - Inclua 1-2 dados relevantes do artigo
   - Tom: profissional mas acessível

3. INSIGHT/VALOR (1-2 parágrafos)
   - O que o leitor ganha lendo isso?
   - Uma dica prática ou reflexão importante
   - Conecte com a realidade do público industrial

4. CTA (call to action)
   - Convide para ler o artigo completo
   - Use emoji de seta 👉 antes do link
   - Ex: "👉 Leia o artigo completo: [link]"

5. HASHTAGS (5-8 hashtags)
   - Mix de hashtags populares e específicas
   - Sempre incluir: #indústria40 #inovação
   - Adicionar específicas do tema
   - Formato: linha separada no final

REGRAS DE FORMATAÇÃO:
- Use emojis com moderação (2-4 no post inteiro)
- Quebre linhas para facilitar leitura
- Não use bullet points - prefira parágrafos curtos
- Máximo 3000 caracteres (limite do LinkedIn)

EXEMPLO DE ESTRUTURA:
"""
[Gancho impactante] 🔍

[Parágrafo contexto 1]

[Parágrafo contexto 2]

[Insight/valor]

[CTA com link]

#hashtag1 #hashtag2 #hashtag3
"""

Retorne APENAS o post pronto, sem explicações.`
    },
    {
      role: 'user',
      content: `Crie um post para LinkedIn baseado neste artigo:

TÍTULO: ${title}

CONTEÚDO:
${content.substring(0, 4000)}

${blogUrl ? `LINK DO ARTIGO: ${blogUrl}` : 'LINK DO ARTIGO: [será adicionado depois]'}`
    }
  ];

  return makeRequest(messages, 1500, 0.7);
};

// Gerar apenas hashtags para LinkedIn
export const generateLinkedInHashtags = async (content) => {
  const messages = [
    {
      role: 'system',
      content: `Gere 8-10 hashtags otimizadas para LinkedIn sobre indústria e tecnologia.

REGRAS:
- Mix de hashtags populares (#inovação, #indústria40, #tecnologia) e específicas do tema
- Sem espaços, tudo minúsculo
- Formato: #hashtag1 #hashtag2 #hashtag3
- Retorne APENAS as hashtags, nada mais`
    },
    {
      role: 'user',
      content: `Gere hashtags para este conteúdo:\n\n${content.substring(0, 1500)}`
    }
  ];

  return makeRequest(messages, 150, 0.5);
};

// ==========================================
// TRADUÇÃO DE CONTEÚDO DO BLOG
// ==========================================

// Traduzir conteúdo do blog para inglês e espanhol
export const translateBlogContent = async (content, onProgress = null) => {
  try {
    const { title, excerpt, content: articleContent, meta_description } = content;

    // FASE 1: Traduzir para inglês
    if (onProgress) onProgress('translating_en', 'Traduzindo para inglês...');

    const enMessages = [
      {
        role: 'system',
        content: `You are a professional translator specializing in technical and industrial content.

TASK: Translate Portuguese content to English maintaining:
- Technical accuracy
- Professional tone
- SEO-friendly structure
- Original markdown formatting

Return ONLY a JSON object with translated fields, no explanations:
{
  "title": "translated title",
  "excerpt": "translated excerpt",
  "content": "translated content with markdown",
  "meta_description": "translated meta description"
}`
      },
      {
        role: 'user',
        content: `Translate this blog post to English:

TITLE: ${title}

EXCERPT: ${excerpt || ''}

META DESCRIPTION: ${meta_description || ''}

CONTENT:
${articleContent}`
      }
    ];

    const enResult = await makeRequest(enMessages, 6000, 0.3);

    let translations_en;
    try {
      translations_en = JSON.parse(enResult.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      console.error('Erro ao parsear tradução EN:', e);
      translations_en = {
        title: title,
        excerpt: excerpt,
        content: articleContent,
        meta_description: meta_description
      };
    }

    // FASE 2: Traduzir para espanhol
    if (onProgress) onProgress('translating_es', 'Traduzindo para espanhol...');

    const esMessages = [
      {
        role: 'system',
        content: `Eres un traductor profesional especializado en contenido técnico e industrial.

TAREA: Traducir contenido del portugués al español manteniendo:
- Precisión técnica
- Tono profesional
- Estructura optimizada para SEO
- Formato markdown original

Devuelve SOLO un objeto JSON con los campos traducidos, sin explicaciones:
{
  "title": "título traducido",
  "excerpt": "resumen traducido",
  "content": "contenido traducido con markdown",
  "meta_description": "meta descripción traducida"
}`
      },
      {
        role: 'user',
        content: `Traduce este post de blog al español:

TÍTULO: ${title}

RESUMEN: ${excerpt || ''}

META DESCRIPCIÓN: ${meta_description || ''}

CONTENIDO:
${articleContent}`
      }
    ];

    const esResult = await makeRequest(esMessages, 6000, 0.3);

    let translations_es;
    try {
      translations_es = JSON.parse(esResult.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      console.error('Erro ao parsear tradução ES:', e);
      translations_es = {
        title: title,
        excerpt: excerpt,
        content: articleContent,
        meta_description: meta_description
      };
    }

    if (onProgress) onProgress('completed', 'Traduções concluídas!');

    return {
      en: {
        title_en: translations_en.title,
        excerpt_en: translations_en.excerpt,
        content_en: translations_en.content,
        meta_description_en: translations_en.meta_description
      },
      es: {
        title_es: translations_es.title,
        excerpt_es: translations_es.excerpt,
        content_es: translations_es.content,
        meta_description_es: translations_es.meta_description
      }
    };

  } catch (error) {
    console.error('Erro ao traduzir conteúdo:', error);
    throw error;
  }
};

// ==========================================
// WHATSAPP - ANÁLISE DE CONVERSAS
// ==========================================

// Processar export de chat do WhatsApp e extrair insights
export const analyzeWhatsAppChat = async (chatContent, onProgress = null) => {
  try {
    // FASE 1: Extrair estrutura e participantes
    if (onProgress) onProgress('parsing', 'Analisando estrutura do chat...');

    const parsingMessages = [
      {
        role: 'system',
        content: `Você é um analista de dados especializado em conversas de grupos de WhatsApp.

SUA TAREFA: Analisar o export de chat e extrair informações estruturadas.

FORMATO DE SAÍDA (JSON):
{
  "periodo": "DD/MM/YYYY a DD/MM/YYYY",
  "total_mensagens": número,
  "participantes_ativos": [
    {"nome": "Nome", "mensagens": número, "cargo_provavel": "CEO/CTO/etc"}
  ],
  "horarios_pico": ["HH:MM - HH:MM"],
  "idiomas": ["português", "inglês", etc]
}

Retorne APENAS o JSON, sem explicações.`
      },
      {
        role: 'user',
        content: `Analise este export de WhatsApp:\n\n${chatContent.substring(0, 15000)}`
      }
    ];

    const estrutura = await makeRequest(parsingMessages, 1000, 0.3);

    // FASE 2: Identificar tópicos e tendências
    if (onProgress) onProgress('topics', 'Identificando tópicos principais...');

    const topicsMessages = [
      {
        role: 'system',
        content: `Você é um analista de tendências em IA e tecnologia.

Analise a conversa de um grupo de WhatsApp de executivos (CEOs, CTOs) discutindo IA e tecnologia.

EXTRAIA:
1. TOP 10 TÓPICOS mais discutidos (com frequência estimada)
2. TECNOLOGIAS/FERRAMENTAS mais mencionadas
3. EMPRESAS/PRODUTOS citados
4. TENDÊNCIAS emergentes identificadas
5. PROBLEMAS/DESAFIOS mencionados pelos participantes

FORMATO DE SAÍDA (JSON):
{
  "topicos": [
    {"titulo": "Tópico", "frequencia": "alta/média/baixa", "resumo": "breve descrição"}
  ],
  "tecnologias": [
    {"nome": "Tecnologia", "mencoes": número_estimado, "contexto": "como é discutida"}
  ],
  "empresas": [
    {"nome": "Empresa", "mencoes": número_estimado, "sentimento": "positivo/neutro/negativo"}
  ],
  "tendencias": [
    {"titulo": "Tendência", "relevancia": "alta/média/baixa", "descricao": "explicação"}
  ],
  "desafios": [
    {"titulo": "Desafio", "frequencia": "alta/média/baixa", "contexto": "como é discutido"}
  ]
}

Retorne APENAS o JSON, sem explicações.`
      },
      {
        role: 'user',
        content: `Analise os tópicos desta conversa de executivos sobre IA:\n\n${chatContent.substring(0, 25000)}`
      }
    ];

    const topicos = await makeRequest(topicsMessages, 2000, 0.5);

    // FASE 3: Extrair insights acionáveis
    if (onProgress) onProgress('insights', 'Extraindo insights estratégicos...');

    const insightsMessages = [
      {
        role: 'system',
        content: `Você é um consultor estratégico de IA para negócios B2B industriais.

CONTEXTO: Você está analisando conversas de um grupo de WhatsApp chamado "Futures" com CEOs, CTOs e líderes de tecnologia discutindo IA.

A VEGA ROBOTICS oferece:
- Plataformas de IA industrial (análise de dados, manutenção preditiva)
- Visão computacional e edge computing
- Engenharia sob demanda
- Consultoria em Indústria 4.0

SUA TAREFA: Extrair insights ACIONÁVEIS que a Vega pode usar para:
1. Prospecção de clientes
2. Criação de conteúdo relevante
3. Desenvolvimento de produtos/serviços
4. Posicionamento de mercado

FORMATO DE SAÍDA (JSON):
{
  "oportunidades_prospeccao": [
    {"insight": "descrição", "acao_sugerida": "o que fazer", "prioridade": "alta/média/baixa"}
  ],
  "ideias_conteudo": [
    {"tema": "título", "formato": "blog/linkedin/webinar", "justificativa": "por que é relevante"}
  ],
  "gaps_mercado": [
    {"necessidade": "descrição", "solucao_potencial": "como Vega pode atender"}
  ],
  "citacoes_relevantes": [
    {"citacao": "texto aproximado", "autor": "nome se identificável", "relevancia": "por que importa"}
  ],
  "resumo_executivo": "Parágrafo de 3-4 linhas com os principais insights"
}

Retorne APENAS o JSON, sem explicações.`
      },
      {
        role: 'user',
        content: `Com base nesta análise de tópicos:\n${topicos}\n\nE no conteúdo da conversa:\n${chatContent.substring(0, 20000)}\n\nExtraia insights estratégicos para a Vega Robotics.`
      }
    ];

    const insights = await makeRequest(insightsMessages, 2500, 0.6);

    if (onProgress) onProgress('concluido', 'Análise concluída!');

    // Parsear JSONs e retornar objeto consolidado
    let estruturaObj = {};
    let topicosObj = {};
    let insightsObj = {};

    try {
      estruturaObj = JSON.parse(estrutura.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      estruturaObj = { raw: estrutura };
    }

    try {
      topicosObj = JSON.parse(topicos.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      topicosObj = { raw: topicos };
    }

    try {
      insightsObj = JSON.parse(insights.replace(/```json\n?/g, '').replace(/```\n?/g, ''));
    } catch (e) {
      insightsObj = { raw: insights };
    }

    return {
      estrutura: estruturaObj,
      topicos: topicosObj,
      insights: insightsObj,
      analyzed_at: new Date().toISOString()
    };

  } catch (error) {
    console.error('Erro na análise do WhatsApp:', error);
    throw error;
  }
};

export default {
  fetchUrlContent,
  fetchPdfContent,
  extractPdfText,
  generatePostFromPdf,
  generateExcerpt,
  generateTitles,
  generatePost,
  generatePostMultiAgent,
  generatePostJornalistico,
  generatePostWithTeam,
  generateMetaDescription,
  generateTags,
  improveText,
  generateLinkedInPost,
  generateLinkedInHashtags,
  translateBlogContent,
  analyzeWhatsAppChat,
  TEAMS_INFO
};
